import { ILanguage, translate } from '@nicejob-library/internationalization';
import React, { FormEvent, ReactElement, useState } from 'react';
import styled from 'styled-components';
import { validatePassword as commonValidatePassword } from '../../../../common/validation/validatePassword';
import { Checkbox, InputField } from '../../core';
import { PasswordField } from '../input/password-field/PasswordField';
import { HiddenInput, PasswordValidation, Row, RowSubmit, SubmitInput } from './Form.styles';

interface RegistrationFormProps {
    brand_name: string;
    post_url: string;
    welcome_text: string;

    email?: string;
    name?: string;
    onSubmit?: (e: FormEvent) => void;
    language: ILanguage;
    /** Whether to show communication preferences checkboxes */
    show_communication_preferences?: boolean;
}

const PRIVACY_POLICY_URL = 'https://nicejob.com/privacy';

const WelcomeText = styled.p`
    font-size: 17px;
    line-height: 1.6;
    color: ${(props): string => props.theme.colors.grey800};
    margin: 0px;
    padding: 10px 20px 30px;
    font-size: 17px;
    text-align: center;
`;

const PreferencesContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    margin: 16px 0;
    background-color: ${(props): string => props.theme.colors.grey50};
    border-radius: 8px;
`;

const PreferencesTitle = styled.p`
    font-size: 11px;
    font-weight: 600;
    color: ${(props): string => props.theme.colors.grey600};
    margin: 0;
    text-transform: uppercase;
    letter-spacing: 0.6px;
`;

const PreferenceCheckbox = styled(Checkbox)`
    align-items: flex-start;

    label {
        font-size: 13px;
        line-height: 18px;
    }
`;

const PreferenceLinkText = styled.a`
    color: inherit;
    text-decoration: underline;
    cursor: pointer;
`;

export function RegistrationForm(props: RegistrationFormProps): ReactElement {
    const {
        brand_name,
        post_url,
        welcome_text,
        onSubmit,
        email: input_email,
        name: input_name,
        show_communication_preferences,
    } = props;

    const [email, setEmail] = useState(props.email);
    const [password, setPassword] = useState('');
    const [validation, setValidation] = useState({
        valid: false,
        message: '',
        color: '',
    });
    const [marketing_opt_in, setMarketingOptIn] = useState(false);
    const [ad_targeting_consent, setAdTargetingConsent] = useState(false);

    function onChange(name: string) {
        return function(value: string): void {
            if (name === 'email') {
                setEmail(value);
            } else if (name === 'password') {
                setPassword(value);
            }

            const { valid, message, color } = commonValidatePassword({
                password: name === 'password' ? value : password,
                email: name === 'email' ? value : email,
                language: props.language,
            });
            setValidation({ valid, message, color });
        };
    }

    function onSubmitHandler(e: FormEvent): void {
        if (onSubmit) {
            onSubmit(e);
        }
    }

    return (
        <div>
            <form onSubmit={onSubmitHandler} action={post_url} method='POST'>
                <WelcomeText>{welcome_text}</WelcomeText>

                <Row>
                    <InputField
                        required
                        variant='stretch'
                        placeholder={translate({
                            namespace: 'login',
                            key: 'registration_form.name',
                            language: props.language,
                        })}
                        type='text'
                        name='name'
                        initial_value={input_name}
                    />
                </Row>
                <Row>
                    <InputField
                        required
                        variant='stretch'
                        placeholder={translate({
                            namespace: 'login',
                            key: 'registration_form.email_address',
                            language: props.language,
                        })}
                        type='email'
                        name='email'
                        initial_value={input_email}
                        onChange={onChange('email')}
                    />
                </Row>
                <Row>
                    <PasswordField required variant='stretch' onChange={onChange('password')} />
                    <PasswordValidation style={{ color: validation.color }}>
                        {validation.message}
                    </PasswordValidation>
                </Row>
                {show_communication_preferences && (
                    <PreferencesContainer>
                        <PreferencesTitle>
                            {translate({
                                namespace: 'login',
                                key: 'onboarding.register.optional_preferences.title',
                                language: props.language,
                            })}
                        </PreferencesTitle>
                        <PreferenceCheckbox
                            value='marketing_opt_in'
                            selected_checkboxes={marketing_opt_in ? ['marketing_opt_in'] : []}
                            onChange={(_, action): void =>
                                setMarketingOptIn(action === 'ADD_ITEM')
                            }
                            label={
                                <span>
                                    {translate({
                                        namespace: 'login',
                                        key: 'onboarding.register.optional_preferences.marketing.text',
                                        language: props.language,
                                    })}{' '}
                                    <PreferenceLinkText
                                        href={PRIVACY_POLICY_URL}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        {translate({
                                            namespace: 'login',
                                            key: 'onboarding.register.optional_preferences.privacy_policy',
                                            language: props.language,
                                        })}
                                    </PreferenceLinkText>{' '}
                                    {translate({
                                        namespace: 'login',
                                        key: 'onboarding.register.optional_preferences.marketing.suffix',
                                        language: props.language,
                                    })}
                                </span>
                            }
                        />
                        <PreferenceCheckbox
                            value='ad_targeting_consent'
                            selected_checkboxes={
                                ad_targeting_consent ? ['ad_targeting_consent'] : []
                            }
                            onChange={(_, action): void =>
                                setAdTargetingConsent(action === 'ADD_ITEM')
                            }
                            label={
                                <span>
                                    {translate({
                                        namespace: 'login',
                                        key: 'onboarding.register.optional_preferences.ad_targeting.text',
                                        language: props.language,
                                    })}{' '}
                                    <PreferenceLinkText
                                        href={PRIVACY_POLICY_URL}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                    >
                                        {translate({
                                            namespace: 'login',
                                            key: 'onboarding.register.optional_preferences.privacy_policy',
                                            language: props.language,
                                        })}
                                    </PreferenceLinkText>
                                    .
                                </span>
                            }
                        />
                        <HiddenInput
                            type='text'
                            name='marketing_opt_in'
                            value={marketing_opt_in ? 'true' : 'false'}
                        />
                        <HiddenInput
                            type='text'
                            name='ad_targeting_consent'
                            value={ad_targeting_consent ? 'true' : 'false'}
                        />
                        <HiddenInput
                            type='text'
                            name='consent_timestamp'
                            value={new Date().toISOString()}
                        />
                    </PreferencesContainer>
                )}
                <RowSubmit>
                    <SubmitInput
                        type='submit'
                        value={translate({
                            namespace: 'login',
                            key: 'registration_form.submit',
                            params: { brand: brand_name },
                            language: props.language,
                        })}
                    />
                </RowSubmit>
                <HiddenInput type='text' name='skip_password_match' value={1} />
                <HiddenInput type='text' name='language_preference' value={props.language} />
            </form>
        </div>
    );
}
